import { Link } from "@tanstack/react-router";
import { useT } from "@/i18n";
import {
  BadgeCheck,
  Briefcase,
  CalendarDays,
  Globe,
  Heart,
  Images,
  MapPin,
  Sparkles,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SOCIAL_LABELS } from "@/components/socials";
import { CATALOG_LABELS, platformIcon, platformDef, isMusicPlatform } from "@/lib/social-platforms";
import { ShareButtons } from "@/components/share-buttons";
import { useMyReferralCode } from "@/hooks/useReferral";
import { useAuth } from "@/hooks/useAuth";
import { Flame } from "lucide-react";
import { formatCount, formatUsdCents, PRO_TYPES, SERVICE_KINDS, OFFER_TYPES } from "@/lib/people";
import type {
  ProfileArticle,
  ProfileSection,
  PublicPageProfile,
  PublicProfileListing,
} from "@/lib/profile.functions";
import { AmbassadorContent } from "@/components/ambassador-content";

type Social = {
  id: string;
  platform: string;
  url: string;
  handle: string | null;
  followers: number | null;
  posts: number | null;
  engagement_rate: number | null;
  verified: boolean;
};
type Service = {
  id: string;
  title: string;
  kind: string;
  description: string | null;
  price_cents: number;
  delivery_days: number;
};
type Post = {
  id: string;
  url: string;
  platform: string;
  title: string | null;
  caption: string | null;
  thumbnail_url: string | null;
  views: number | null;
  likes: number | null;
};
type Offer = {
  id: string;
  title: string;
  offer_type: string;
  description: string | null;
  budget_cents: number;
  slots: number;
};

export type PublicProfileData = {
  profile: PublicPageProfile;
  socials: Social[];
  services: Service[];
  posts: Post[];
  offers: Offer[];
  sections?: ProfileSection[];
  articles?: ProfileArticle[];
  listings?: PublicProfileListing[];
};

export function PublicProfileView({
  data,
  variant,
  path,
}: {
  data: PublicProfileData;
  variant: "user" | "pro";
  path: string;
}) {
  const { t } = useT();
  const { profile, socials, services, posts, offers } = data;
  const sections = data.sections ?? [];
  const articles = data.articles ?? [];
  const listings = data.listings ?? [];
  const isAmbassador = profile.is_ambassador === true;
  const { user } = useAuth();
  const showJoinCta = !user;
  const viewerRef = useMyReferralCode();
  // The page owner always gets credit for signups coming from their page.
  const myRef = profile.referral_code ?? viewerRef;
  const isPro = variant === "pro";

  const name = isPro
    ? profile.pro_name || profile.display_name || `@${profile.handle}`
    : profile.display_name || `@${profile.handle}`;
  const avatar = isPro ? profile.pro_logo_url || profile.avatar_url : profile.avatar_url;
  const bio = isPro ? profile.pro_bio || profile.bio : profile.bio;
  const website = isPro ? profile.pro_website || profile.website_url : profile.website_url;

  const totalFollowers = socials.reduce((n, s) => n + (s.followers ?? 0), 0);
  const totalPosts = socials.reduce((n, s) => n + (s.posts ?? 0), 0);
  const totalViews = posts.reduce((n, p) => n + (p.views ?? 0), 0);
  const cheapest = services.length ? Math.min(...services.map((s) => s.price_cents)) : null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <header className="card-surface p-6">
        <div className="flex flex-wrap items-start gap-5">
          {avatar ? (
            <img
              src={avatar}
              alt={`${name} profile picture`}
              className="size-28 rounded-2xl object-cover"
            />
          ) : (
            <div className="flex size-28 items-center justify-center rounded-2xl bg-accent text-4xl font-bold text-accent-foreground">
              {name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-3xl font-bold sm:text-4xl">{name}</h1>
            <p className="text-sm text-muted-foreground">
              @{profile.slug ?? profile.handle}
              {isPro && profile.pro_type
                ? ` · ${PRO_TYPES.find((pt) => pt.value === profile.pro_type)?.label ?? t("profiles.professional")}`
                : ""}
            </p>
            {isAmbassador && (
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary-foreground">
                <BadgeCheck className="size-4" />
                {profile.ambassador_title || t("profiles.ambBadge")}
              </p>
            )}
            {profile.headline && <p className="mt-2 text-lg">{profile.headline}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {profile.open_to_work && !isPro && (
                <Badge variant="secondary">{t("profiles.openToCollaborations")}</Badge>
              )}
              {(profile.niches ?? []).slice(0, 6).map((n) => (
                <Badge key={n} variant="outline">
                  {n}
                </Badge>
              ))}
              {profile.country_code && (
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <MapPin className="size-4" /> {profile.country_code}
                </span>
              )}
              {website && (
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex items-center gap-1 text-sm text-primary underline-offset-2 hover:underline"
                >
                  <Globe className="size-4" /> {t("profiles.website")}
                </a>
              )}
              {profile.created_at && (
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <CalendarDays className="size-4" /> {t("profiles.joined")}{" "}
                  {new Date(profile.created_at).toLocaleDateString("en-GB", {
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              )}
            </div>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto">
            <Button asChild variant="hot" className="w-full sm:w-auto">
              <Link to="/marketplace">
                {isPro ? t("profiles.seeOpenOffers") : t("profiles.workWithCreator")}
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto">
              <Link to="/app/messages" search={{ to: profile.id }}>
                {t("messaging.messageMember")}
              </Link>
            </Button>
          </div>
        </div>

        <ShareButtons
          className="mt-5 border-t border-border pt-4"
          path={path}
          refCode={myRef}
          title={`${name} ${t("profiles.onBehotNow")}`}
        />
      </header>

      {showJoinCta && (
        <div className="card-surface mt-4 flex flex-col items-center gap-3 border-primary/30 bg-gradient-to-r from-primary/10 via-transparent to-signal/10 p-5 text-center sm:flex-row sm:text-left">
          <Flame className="size-8 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="font-display text-lg font-bold">
              {t("profiles.joinCtaTitle", { name })}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("profiles.joinCtaSubtitle")}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button asChild variant="hot">
              <Link to="/login">
                {t("profiles.joinCtaRegister")}
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/login">{t("profiles.joinCtaLogin")}</Link>
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4 grid gap-4 sm:grid-cols-4">
        <Stat
          icon={<Users className="size-5 text-primary" />}
          value={formatCount(totalFollowers)}
          label={t("profiles.totalAudience")}
        />
        <Stat
          icon={<Images className="size-5 text-signal" />}
          value={formatCount(totalPosts)}
          label={t("profiles.publicPosts")}
        />
        <Stat
          icon={<Heart className="size-5 text-primary" />}
          value={formatCount(totalViews)}
          label={t("profiles.showcaseViews")}
        />
        <Stat
          icon={<Sparkles className="size-5 text-success" />}
          value={cheapest != null ? formatUsdCents(cheapest) : "—"}
          label={t("profiles.fromPerService")}
        />
      </div>

      {bio && (
        <section className="card-surface mt-4 p-6">
          <h2 className="font-display text-xl font-bold">{t("profiles.about")}</h2>
          <p className="mt-2 whitespace-pre-line text-muted-foreground">{bio}</p>
          {(profile.languages ?? []).length > 0 && (
            <p className="mt-3 text-sm text-muted-foreground">
              {t("profiles.languages")}: {(profile.languages ?? []).join(", ")}
            </p>
          )}
        </section>
      )}

      {listings.length > 0 && (
        <section className="card-surface mt-4 p-6">
          <h2 className="font-display text-xl font-bold">{t("profiles.activeListings")}</h2>
          <p className="text-sm text-muted-foreground">{t("profiles.activeListingsSubtitle")}</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {listings.map((l) => (
              <Link
                key={l.id}
                to="/p/$slug"
                params={{ slug: l.slug }}
                className="flex items-center gap-3 rounded-xl border border-border p-3 transition hover:border-primary"
              >
                {l.logo_url || l.cover_url ? (
                  <img
                    src={(l.logo_url || l.cover_url) as string}
                    alt={l.name}
                    loading="lazy"
                    className="size-12 shrink-0 rounded-lg object-cover"
                  />
                ) : (
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-accent font-display font-bold text-accent-foreground">
                    {l.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{l.name}</p>
                  {l.headline && (
                    <p className="line-clamp-1 text-xs text-muted-foreground">{l.headline}</p>
                  )}
                </div>
                <Flame className="size-4 shrink-0 text-primary" />
              </Link>
            ))}
          </div>
        </section>
      )}

      <AmbassadorContent sections={sections} articles={articles} />

      {socials.length > 0 && (
        <ChannelGroups socials={socials} />
      )}

      {posts.length > 0 && (
        <section className="card-surface mt-4 p-6">
          <h2 className="font-display text-xl font-bold">{t("profiles.showcase")}</h2>
          <p className="text-sm text-muted-foreground">{t("profiles.selectedPastWork")}</p>
          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            {posts.map((p) => (
              <a
                key={p.id}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="overflow-hidden rounded-xl border border-border transition hover:border-primary"
              >
                {p.thumbnail_url ? (
                  <img
                    src={p.thumbnail_url}
                    alt={p.title ?? `Post by ${name}`}
                    loading="lazy"
                    className="h-44 w-full object-cover"
                  />
                ) : (
                  <div className="flex h-44 items-center justify-center bg-muted text-sm text-muted-foreground">
                    {CATALOG_LABELS[p.platform] ?? SOCIAL_LABELS[p.platform] ?? p.platform}
                  </div>
                )}
                <div className="space-y-1 p-3">
                  <p className="line-clamp-2 text-sm font-medium">{p.title ?? p.url}</p>
                  {p.caption && (
                    <p className="line-clamp-2 text-xs text-muted-foreground">{p.caption}</p>
                  )}
                  <div className="flex gap-3 pt-1 text-xs text-muted-foreground">
                    {p.views != null && (
                      <span>{t("profiles.viewsCount", { n: formatCount(p.views) })}</span>
                    )}
                    {p.likes != null && (
                      <span>{t("profiles.likesCount", { n: formatCount(p.likes) })}</span>
                    )}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {services.length > 0 && (
        <section className="card-surface mt-4 p-6">
          <h2 className="font-display text-xl font-bold">{t("profiles.rateCard")}</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {services.map((s) => (
              <div key={s.id} className="rounded-xl border border-border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-display font-semibold">{s.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {SERVICE_KINDS.find((k) => k.value === s.kind)?.label ?? s.kind} ·{" "}
                      {t("profiles.dayDelivery", { n: s.delivery_days })}
                    </p>
                  </div>
                  <p className="font-display text-lg font-bold text-primary">
                    {formatUsdCents(s.price_cents)}
                  </p>
                </div>
                {s.description && (
                  <p className="mt-2 text-sm text-muted-foreground">{s.description}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {isPro && offers.length > 0 && (
        <section className="card-surface mt-4 p-6">
          <h2 className="font-display text-xl font-bold">{t("profiles.openOffers")}</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {offers.map((o) => (
              <div key={o.id} className="rounded-xl border border-border p-4">
                <p className="flex items-center gap-2 font-display font-semibold">
                  <Briefcase className="size-4 text-primary" /> {o.title}
                </p>
                <p className="text-xs text-muted-foreground">
                  {OFFER_TYPES.find((ot) => ot.value === o.offer_type)?.label ?? o.offer_type} ·{" "}
                  {t("profiles.slotsCount", { n: o.slots })}
                </p>
                {o.description && (
                  <p className="mt-2 text-sm text-muted-foreground">{o.description}</p>
                )}
                <p className="mt-2 font-display font-bold text-primary">
                  {formatUsdCents(o.budget_cents)}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function ChannelGroups({ socials }: { socials: Social[] }) {
  const { t } = useT();
  const groups: { key: "music" | "social" | "other"; title: string; items: Social[] }[] = [
    { key: "music", title: t("profiles.musicChannels"), items: [] },
    { key: "social", title: t("profiles.socialChannels"), items: [] },
    { key: "other", title: t("profiles.webLinks"), items: [] },
  ];
  for (const s of socials) {
    const group = platformDef(s.platform)?.group ?? (SOCIAL_LABELS[s.platform] ? "social" : "other");
    groups.find((g) => g.key === group)?.items.push(s);
  }
  return (
    <>
      {groups
        .filter((g) => g.items.length > 0)
        .map((g) => (
          <section key={g.key} className="card-surface mt-4 p-6">
            <h2 className="font-display text-xl font-bold">{g.title}</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {g.items.map((s) => (
                <ChannelCard key={s.id} social={s} />
              ))}
            </div>
          </section>
        ))}
    </>
  );
}

function ChannelCard({ social: s }: { social: Social }) {
  const { t } = useT();
  const Icon = platformIcon(s.platform);
  return (
    <a
      href={s.url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="rounded-xl border border-border p-4 transition hover:border-primary"
    >
      <p className="flex items-center gap-1.5 font-display font-semibold">
        <Icon className="size-4 shrink-0 text-primary" />
        {CATALOG_LABELS[s.platform] ?? SOCIAL_LABELS[s.platform] ?? s.platform}
        {s.verified && <BadgeCheck className="size-4 text-primary" />}
      </p>
      <p className="text-xs text-muted-foreground">{s.handle ? `@${s.handle}` : s.url}</p>
      {!isMusicPlatform(s.platform) && (
        <div className="mt-2 flex gap-4 text-sm">
          <span className="flex items-center gap-1.5">
            <Users className="size-4 text-primary" /> {formatCount(s.followers)}
          </span>
          <span className="flex items-center gap-1.5">
            <Images className="size-4 text-signal" /> {formatCount(s.posts)}
          </span>
          {s.engagement_rate != null && (
            <span className="text-muted-foreground">
              {t("profiles.engagementPercent", { n: s.engagement_rate })}
            </span>
          )}
        </div>
      )}
    </a>
  );
}

function Stat({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="card-surface p-5">
      {icon}
      <p className="mt-2 font-display text-2xl font-bold">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}
